import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { AdditionalDetailsData, PaymentAction, UIElement } from '@adyen/adyen-web';

import {
  ESdkEvents,
  EventParams,
  AdyenFinalizePaymentResponse,
  GetAdyenPaymentStatusResponse,
  AppchargeErrorType,
} from '../../../../../contracts';
import { sendFEEvent } from '../../../../../eventUtil';
import { useErrorBoundary } from 'react-error-boundary';
import { useAdyenCheckout } from './use-adyen-checkout';
import { useAPI } from '../../../../../services/api.service';
import { useCheckout } from '../../../../../hooks/use-checkout';
import { SavedCardFormProps } from '../../SavedCards/SavedCards';
import { usePaymentState } from '../../../hooks/use-payment-state';
import { useOrderPolling } from '../../../hooks/use-order-polling';
import { ADYEN_CARD_CONFIGURATION, createAdyenSavedCardFinalizeRequestBody } from '../utils';
import { generateAppchargeErrorMessage } from '../../../../../utils/generate-appcharge-error-message';

export function useAdyenSavedCardSubmit() {
  const isAdyenPaymentInProgress = useRef(false);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const { selectedPaymentMethod, checkoutData, getEventParams } = useCheckout();
  const { checkoutInstance } = useAdyenCheckout(selectedPaymentMethod);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const { startOrderPolling } = useOrderPolling();
  const { API } = useAPI();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();
  const [threeDSComponent, setThreeDSComponent] = useState<UIElement | null>(null);
  const savedCardEventParams: Partial<EventParams> = {
    isSavedPaymentMethodUsed: true,
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    setThreeDSComponent(null);
    isAdyenPaymentInProgress.current = false;
  };

  const handleSubmit = async (formValues: SavedCardFormProps) => {
    if (isAdyenPaymentInProgress.current) {
      return;
    }
    isAdyenPaymentInProgress.current = true;
    setIsPaymentLoading(true);
    try {
      const finalizePaymentRequestBody = createAdyenSavedCardFinalizeRequestBody(
        formValues,
        selectedPaymentMethod!
      );
      const finalizePaymentResponse = await API.finalizePayment<AdyenFinalizePaymentResponse>(
        finalizePaymentRequestBody
      );
      handleServerResponse(finalizePaymentResponse);
    } catch (error: any) {
      const message = error?.response?.message;
      showBoundary(t('unableToProcessPayment'));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: message,
        ...savedCardEventParams,
        error: {
          isFatal: true,
          reason: message,
        },
      });
    }
  };

  const handleServerResponse = (finalizePaymentResponse: AdyenFinalizePaymentResponse) => {
    const { status, message, action } = finalizePaymentResponse.adyen;
    if (status === 'succeeded') {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...savedCardEventParams,
      });
      startOrderPolling(checkoutData?.orderId!, savedCardEventParams);
    } else if (status === 'redirect') {
      if (action) {
        handle3DSFlow(action);
      } else {
        handleError(finalizePaymentResponse.appChargeErrorType);
      }
    } else if (status === 'failed') {
      handleError(finalizePaymentResponse.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...savedCardEventParams,
        reason: message,
      });
    }
  };

  const onAdditionalDetails = async (state: AdditionalDetailsData) => {
    const threeDSResult = state.data.details.threeDSResult;
    if (!threeDSResult) {
      handleError();
      return;
    }

    try {
      const paymentStatusResponse = await API.getPaymentStatus<GetAdyenPaymentStatusResponse>(
        threeDSResult,
        selectedPaymentMethod?.id as string
      );
      const resultCode = paymentStatusResponse.resultCode;
      if (resultCode === 'Authorised') {
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
          ...getEventParams(),
          ...savedCardEventParams,
        });
        startOrderPolling(checkoutData!.orderId, savedCardEventParams);
      } else {
        setThreeDSComponent(null);
        handleError();
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          ...savedCardEventParams,
        });
      }
    } catch (error) {
      handleError();
    }
  };

  const handle3DSFlow = (action: PaymentAction) => {
    const actionComponent = checkoutInstance!.createFromAction(action, {
      ...ADYEN_CARD_CONFIGURATION,
      onAdditionalDetails,
    });
    setThreeDSComponent(actionComponent);
  };

  return {
    handleSubmit,
    threeDSComponent,
  };
}
